import React, { useState, useEffect } from "react";
import useMediaQuery from "hooks/useMediaQuery";
import { BreakPoints } from "utils/BreakPoints";
import GlobalStyle from "styles/global";
import styled from "styled-components";
import data from "./data/data.json";
import { Helmet } from "react-helmet";
import amplitude from "amplitude-js";
import TagManager from "react-gtm-module";
import { Container, Row, Col } from "components/Grid";
import { BodyCallCta } from "components/Partials";
import "index.css";
import find from "lodash/find";
import { _capitalizeWords } from "./utils/misc";
import drugparams from "./data/drugterms.json";
import { tagMangerId, googleOptimizeId } from "./utils/environmentUrls";
import { ISource } from "./types";

const Footer = React.lazy(() => import("components/Partials/Footer"));
const Separation = React.lazy(() => import("components/Partials/Separation"));
const Header = React.lazy(() => import("components/Header/Header"));
const Hero = React.lazy(() => import("components/Hero/Hero"));
const TreatmentBlock = React.lazy(() => import("components/TreatmentBlock"));
const ThreePhotosBlock = React.lazy(
  () => import("components/ThreePhotosBlock")
);
const CallPhoneBlock = React.lazy(() => import("components/CallPhoneBlock"));
const FreeSupportBlock = React.lazy(() => import("components/FreeSupportBlock"));
const InfoBlock = React.lazy(() => import("components/InfoBlock"));
const LeadingTreatmentBlock = React.lazy(() => import("components/LeadingTreatmentBlock"));

const SUInfoBlock = React.lazy(() => import("components/SUInfoBlock"));
const InfoIconBlock = React.lazy(() => import("components/InfoIconBlock"));
const InsuranceBlock = React.lazy(() => import("components/InsuranceBlock"));
const QuotesBlock = React.lazy(() => import("components/QuotesBlock"));
// const FixedCTA = React.lazy(() => import("components/FixedCta"));

const tagManagerArgs = {
  gtmId: tagMangerId,
};

TagManager.initialize(tagManagerArgs);

const SepCta = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 210px;
  background-color: #4665b4;

  @media (max-width: 768px) {
    padding: 20px 0;
    height: auto;
    * {
      align-items: center;
    }
    > div > div {
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
    }
  }

  &::after {
    content: "";
    width: 100%;
    height: 14px;
    position: absolute;
    bottom: 100%;
    background: linear-gradient(270deg, #74a0ca 0%, #4665b4 100%);
    mix-blend-mode: normal;
    opacity: 0.6;
  }

  p {
    margin-left: -1.5rem;
    color: white;
    &:nth-child(1) {
      font-size: 40px;
      line-height: 44px;
      font-weight: 500;
      margin-bottom: 15px;
    }
    &:nth-child(2) {
      font-size: 20px;
      line-height: 28px;
      font-weight: 400;
    }
    @media (max-width: 768px) {
      margin-left: 0;
    }
  }
`;

const ButtonHolder = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

declare global {
  interface Window {
    intercomSettings: any;
  }
}

// Ad dynamic terms
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);

let drugTerm;
let titleObj;
let allTerms;

let adDataWebsite = find(drugparams, function (n) {
  if (n.website === process.env.REACT_APP_WEBSITE) {
    return n;
  }
});

if (adDataWebsite) {
  allTerms = adDataWebsite.terms;
}

if (urlParams.has("adgid")) {
  titleObj = find(allTerms, (o) => {
    return o.id === urlParams.get("adgid");
  });
  if (titleObj) {
    let capWordsh1 = _capitalizeWords(titleObj.drug);
    drugTerm = capWordsh1;
  }
}

const App: React.FC<any> = () => {
  const [website, setWebsite]: any = useState(process.env.REACT_APP_WEBSITE);
  const [metadata, setMetadata]: any = useState({});
  const [isNewport, setIsNewport]: any = useState(false);
  const [isSerenity, setIsSerenity]: any = useState(false);
  const [isIndiana, setIsIndiana]: any = useState(false);
  const [header, setHeader]: any = useState({});
  const [hero, setHero]: any = useState({});
  const [infoIconBlock, setInfoIconBlock]: any = useState({});
  const [insuranceBlock, setInsuranceBlock]: any = useState({});
  const [threePhotosBlock, setThreePhotosBlock]: any = useState({});
  const [separation, setSeparation]: any = useState({});
  const [infoBlock, setInfoBlock]: any = useState({});
  const [leadingTreatmentBlock, setLeadingTreatmentBlock]: any = useState({});
  const [quotesBlock, setQuotesBlock]: any = useState({});
  const [treatmentBlock, setTreatmentBlock]: any = useState({});
  const [callPhoneBlock, setCallPhoneBlock]: any = useState({});
  const [freeSupportBlock, setFreeSupportBlock]: any = useState({});
  const [footer, setFooter]: any = useState({});
  const [isAutism, setIsAutism]: any = useState(false);
  const [isAutismModelOpen, setAutismModalOpen] = useState(false);

  const isDesktopBreakPoint = useMediaQuery(BreakPoints.DESKTOP);
  const isMobileBreakPoint = useMediaQuery(BreakPoints.MOBILE);
  const [isDesktop, setDesktop] = useState(false);
  const [isMobile, setMobile] = useState(false);

  useEffect(() => {
    if (!window.intercomSettings) {
      window.intercomSettings = {
        app_id: "dbktifl9",
        vertical_padding: 100,
      };
    }
  }, []);

  useEffect(() => {
    if (isDesktopBreakPoint && isMobileBreakPoint) {
      setDesktop(true);
      setMobile(true);
    } else {
      setDesktop(false);
      setMobile(false);
    }
  }, [isDesktopBreakPoint, isMobileBreakPoint]);

  useEffect(() => {
    const API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY;
    const PAGE = process.env.REACT_APP_WEBSITE;
    if (API_KEY && PAGE) {
      amplitude.getInstance().init(API_KEY);
      const event = "LandingPageView";
      const data = {
        page: website ? "Zinnia " + website : "Zinnia " + PAGE,
        website: window.location.pathname,
        action: "Page View",
      };
      amplitude.getInstance().logEvent(event, data);
    }
  }, [website]);

  useEffect(() => {
    if (website === "Serenity") {
      setIsNewport(false);
      setIsSerenity(true);
      setIsAutism(false);
      setIsIndiana(false);
    } else if (website === "Newport") {
      setIsNewport(true);
      setIsSerenity(false);
      setIsAutism(false);
      setIsIndiana(false);
    } else if (website === "Indiana") {
      setIsIndiana(true);
      setIsNewport(false);
      setIsSerenity(false);
      setIsAutism(false);
    } else if (website === "Autism") {
      setIsIndiana(false);
      setIsNewport(false);
      setIsSerenity(false);
      setIsAutism(true);
    } else {
      setIsNewport(false);
      setIsSerenity(false);
      setIsAutism(false);
      setIsIndiana(false);
    }
  }, [website]);

  useEffect(() => {
    for (let i = 0; i < data.length; i++) {
      if (data[i]?.website === website) {
        for (let j = 0; j < data[i]["data"].length; j++) {
          switch (data[i]["data"][j].component) {
            case "Header":
              setHeader(data[i]["data"][j].information);
              break;
            case "Metadata":
              setMetadata(data[i]["data"][j].information);
              break;
            case "Hero":
              setHero(data[i]["data"][j].information);
              break;
            case "InsuranceBlock":
              setInsuranceBlock(data[i]["data"][j].information);
              break;
            case "Separation":
              setSeparation(data[i]["data"][j].information);
              break;
              case "InfoBlock":
              setInfoBlock(data[i]["data"][j].information);
              break;
            case "LeadingTreatmentBlock":
              setLeadingTreatmentBlock(data[i]["data"][j].information);
              break;
            case "InfoIconBlock":
              setInfoIconBlock(data[i]["data"][j].information);
              break;
            case "ThreePhotosBlock":
              setThreePhotosBlock(data[i]["data"][j].information);
              break;
            case "QuotesBlock":
              setQuotesBlock(data[i]["data"][j].information);
              break;
            case "TreatmentBlock":
              setTreatmentBlock(data[i]["data"][j].information);
              break;
            case "CallPhoneBlock":
              setCallPhoneBlock(data[i]["data"][j].information);
              break;
            case "FreeSupportBlock":
              setFreeSupportBlock(data[i]["data"][j].information);
              break;
            case "Footer":
              setFooter(data[i]["data"][j].information);
              break;
          }
        }
      }
    }
  }, [website]);

  return (
    <>
      <GlobalStyle />
      <Helmet>
        <title>{metadata.title}</title>
        <meta content={metadata.title} property="og:title" />
        <meta content={metadata.title} property="twitter:title" />
        <meta name="robots" content="nofollow, noindex" />
        {/* <!-- anti-flicker snippet (recommended)  --> */}
        <script type="text/javascript">{`
          (function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
          h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};
          (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
          })(window,document.documentElement,'async-hide','dataLayer',4000,
          {'${googleOptimizeId}':true});
      `}</script>
        {/* Google Optimise */}
        <script
          src={`https://www.googleoptimize.com/optimize.js?id=${googleOptimizeId}`}
        ></script>
        <link
          rel="preconnect"
          crossOrigin="anonymous"
          href="https://fonts.googleapis.com"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto+Flex:opsz,wght@8..144,300;8..144,400;8..144,500;8..144,600;8..144,700&display=swap"
          rel="stylesheet"
        />
        <link
          rel="icon"
          href="https://cdn.zinniahealth.com/wp-content/uploads/20220214183452/cropped-zinnia-favicon-1-32x32-1.png"
          sizes="32x32"
        />
        <link
          rel="icon"
          href="https://cdn.zinniahealth.com/wp-content/uploads/20220214183451/cropped-zinnia-favicon-1-192x192-1.png"
          sizes="192x192"
        />
        <link
          rel="apple-touch-icon"
          href="https://cdn.zinniahealth.com/wp-content/uploads/20220214183647/cropped-zinnia-favicon-1-180x180-1.png"
        />
      </Helmet>

      <Header
        logo={header.logo}
        altLogo={header.altLogo}
        ctaText={header.ctaText}
        ctaPhoneText={header.ctaPhoneText}
        ctaPhoneLink={header.ctaPhoneLink}
        ctaColor={header.ctaColor}
        ctaBackgroundColor={header.ctaBackgroundColor}
        ctaSeparatorColor={header.ctaSeparatorColor}
        infoBarText={header.infoBarText}
        infoBarIcon={header.infoBarIcon}
        infoBarTextColor={header.infoBarTextColor}
        infoBarBackground={header.infoBarBackground}
        website={website}
        isAutism={isAutism}
        hasLink={header.href}
        backgroundHeader={
          header.backgroundGradient
            ? header.backgroundGradient
            : hero.backgroundGradient
        }
      />

      {console.log(hero)}
      <Hero
        mobileHero={hero.mobileHero}
        heroTitle={hero.heroTitle}
        titleColor={hero.titleColor}
        reviewsCountMessage={hero.reviewsCountMessage}
        subHeadingColor={hero.subHeadingColor}
        heroTitleText={hero.heroTitleText}
        ctaText={hero.ctaText}
        ctaPhoneText={hero.ctaPhoneText}
        ctaPhoneLink={hero.ctaPhoneLink}
        ctaBackground={hero.ctaBackground}
        ctaSeparator={hero.ctaSeparator}
        heroBackground={hero.heroBackground}
        heroImage={hero.heroImage}
        heroImageDesktop={hero.heroImageDesktop}
        gradient={hero.gradient}
        altImage={hero.altImage}
        isSerenity={isSerenity}
        isNewport={isNewport}
        isIndiana={isIndiana}
        isAutism={isAutism}
        showPhone={isAutism ? true : true}
        website={website}
        gradientBackground={hero.gradientBackground}
        setAutismModalOpen={setAutismModalOpen}
      />

      {!isAutism && (
        <>
          <SUInfoBlock
            drugTerm={drugTerm}
            color={infoBlock.color}
            paragraphColor={infoBlock.paragraphColor}
            ctaText={"CALL TO LEARN MORE"}
            ctaPhoneText={hero.ctaPhoneText}
            ctaPhoneLink={hero.ctaPhoneLink}
            ctaBackground={hero.ctaBackground}
            ctaSeparator={hero.ctaSeparator}
            heroText={hero.heroText}
          />
          <InfoIconBlock
            mainColor={infoIconBlock.mainColor}
            mainTitle={infoIconBlock.mainTitle}
            iconInfo1={infoIconBlock.iconInfo1}
            iconInfo2={infoIconBlock.iconInfo2}
            iconInfo3={infoIconBlock.iconInfo3}
            iconInfo4={infoIconBlock.iconInfo4}
            iconInfo1v2={infoIconBlock.iconInfo1v2}
            iconInfo2v2={infoIconBlock.iconInfo2v2}
            iconInfo3v2={infoIconBlock.iconInfo3v2}
            iconInfo4v2={infoIconBlock.iconInfo4v2}
          />
          <Separation height={separation.height} color={separation.color} />

          <LeadingTreatmentBlock
          title={leadingTreatmentBlock.title}
          mainColor={leadingTreatmentBlock.mainColor}
          checksColor={leadingTreatmentBlock.checksColor}
          reasons = {leadingTreatmentBlock.reasons}
          rateComparison={{
            left: {
              rate: 52,
              title: 'National Avg. Completion Rate'
            },
            right: {
              rate: 87,
              title: 'Our Completion Rate'
            }
          }}
          ctaText={leadingTreatmentBlock.ctaText}
          ctaPhoneText={leadingTreatmentBlock.ctaPhoneText}
          ctaPhoneLink={leadingTreatmentBlock.ctaPhoneLink}
          ctaBackground={leadingTreatmentBlock.ctaBackground}
          satisfactionBackground={leadingTreatmentBlock.satisfactionBackground}
          satisfactionRate={leadingTreatmentBlock.satisfactionRate}
          />

          <InsuranceBlock
            backgroundColor={insuranceBlock.backgroundColor}
            insuranceBackgroundColor={insuranceBlock.insuranceBackgroundColor}
            title={insuranceBlock.title}
            content={insuranceBlock.content}
            form={insuranceBlock.form}
            ctaText={insuranceBlock.ctaText}
            ctaPhoneText={insuranceBlock.ctaPhoneText}
            ctaPhoneLink={insuranceBlock.ctaPhoneLink}
            ctaBackground={insuranceBlock.ctaBackground}
            ctaSeparator={insuranceBlock.ctaSeparator}
            code={insuranceBlock.code as ISource}
            onlineFormColor={insuranceBlock.onlineFormColor}
            isAutism={isAutism}
            website={website}
            isNewport={isNewport}
          />
        </>
      )}

      <TreatmentBlock
        backgroundColor={treatmentBlock.backgroundColor}
        titleColor={treatmentBlock.titleColor}
        title={treatmentBlock.title}
        paragraph={treatmentBlock.paragraph}
        moreText={treatmentBlock.moreText}
        circleColor={treatmentBlock.circleColor}
        alignment1={treatmentBlock.alignment1}
        title1={treatmentBlock.title1}
        content1={treatmentBlock.content1}
        image1={treatmentBlock.image1}
        icon1={treatmentBlock.icon1}
        alignment2={treatmentBlock.alignment2}
        title2={treatmentBlock.title2}
        content2={treatmentBlock.content2}
        image2={treatmentBlock.image2}
        icon2={treatmentBlock.icon2}
        alignment3={treatmentBlock.alignment3}
        title3={treatmentBlock.title3}
        content3={treatmentBlock.content3}
        image3={treatmentBlock.image3}
        icon3={treatmentBlock.icon3}
        amenities={treatmentBlock.amenities}
        alignment4={treatmentBlock.alignment4}
        title4={treatmentBlock.title4}
        content4={treatmentBlock.content4}
        image4={treatmentBlock.image4}
        icon4={treatmentBlock.icon4}
        alignment5={treatmentBlock.alignment5}
        title5={treatmentBlock.title5}
        content5={treatmentBlock.content5}
        image5={treatmentBlock.image5}
        icon5={treatmentBlock.icon5}
        isNewport={isNewport}
        isSerenity={isSerenity}
        isAutism={isAutism}
        ctaText={treatmentBlock.ctaText}
        ctaPhoneText={treatmentBlock.ctaPhoneText}
        ctaPhoneLink={treatmentBlock.ctaPhoneLink}
        ctaBackground={treatmentBlock.ctaBackground}
        ctaSeparator={treatmentBlock.ctaSeparator}
        website={website}
        subTitleColor={treatmentBlock.subTitleColor}
        paragraphColor={treatmentBlock.paragraphColor}
      />

      {isAutism && (
        <>
          <SepCta>
            <Container>
              <Row>
                <Col size={!isMobile ? 5 : 12}>
                  <p>
                    Paying for care <br /> shouldn’t be an issue.
                  </p>
                  <p>We accept most insurance providers.</p>
                </Col>
                <Col size={!isMobile ? 6 : 12}>
                  <ButtonHolder>
                    <BodyCallCta
                      hasNumber={isMobile ? false : true}
                      backgroundColor={"#5BA731"}
                      separatorColor={"#FFFFFF"}
                      text={"Call to learn more"}
                      phoneText={"657-584-5792"}
                      ctaPhoneLink={"tel:6575845792"}
                      showPhone
                    />
                  </ButtonHolder>
                </Col>
              </Row>
            </Container>
          </SepCta>
        </>
      )}

      {isAutism && (
        <ThreePhotosBlock
          titleColor={threePhotosBlock.titleColor}
          name1={threePhotosBlock.name1}
          photo1={threePhotosBlock.photo1}
          name2={threePhotosBlock.name2}
          photo2={threePhotosBlock.photo2}
          name3={threePhotosBlock.name3}
          photo3={threePhotosBlock.photo3}
          isOnlyThreePhotos={isAutism ? false : true}
          bigTitle={threePhotosBlock.bigTitle}
          name4={threePhotosBlock.name4}
          photo4={threePhotosBlock.photo4}
          name5={threePhotosBlock.name5}
          photo5={threePhotosBlock.photo5}
          title1={threePhotosBlock.title1}
          title2={threePhotosBlock.title2}
          title3={threePhotosBlock.title3}
          title4={threePhotosBlock.title4}
          title5={threePhotosBlock.title5}
          isAutism={isAutism}
        />
      )}

      {!isAutism && (
        <>
          <QuotesBlock
            mainColor={quotesBlock.mainColor}
            title={quotesBlock.title}
            stars={quotesBlock.stars}
            reviews={quotesBlock.reviews}
            quote1={quotesBlock.quote1}
            quote2={quotesBlock.quote2}
            quote3={quotesBlock.quote3}
            quote1by={quotesBlock.quote1by}
            quote2by={quotesBlock.quote2by}
            quote3by={quotesBlock.quote3by}
          />
          <FreeSupportBlock
            title={freeSupportBlock.title}
            mainColor={freeSupportBlock.mainColor}
            ctaText={freeSupportBlock.ctaText}
            ctaPhoneText={freeSupportBlock.ctaPhoneText}
            ctaPhoneLink={freeSupportBlock.ctaPhoneLink}
            ctaBackground={freeSupportBlock.ctaBackground}
            photoGallery={freeSupportBlock.photoGallery}
          />
        </>
      )}

      <Footer
        hideSeals={isAutism}
        copyright={footer.copyright}
        background={footer.background}
        legitScript={footer.legitScriptId}
        termsAndConditionsLink={footer.termsAndConditionsLink}
        privacyPolicyLink={footer.privacyPolicyLink}
      />
    </>
  );
};

export default App;
